// src/utils/generatePdfReport.js
// ===================================================================================
// Report PDF: Settimanale, Mensile, Statistiche — versione completa e coerente
// - Allineamento PRESENZE mensili per DATA (fallback per indice solo se necessario)
// - Intestazioni giorni in formato GG/MM (no anno) nel mensile
// - Colonna Nome più larga per evitare a capo su nomi lunghi
// - Header CEI con contrasto automatico; banda superiore con logo ai lati
// - Foto studente (immagine o placeholder tondo grigio con iniziale nera)
// - Messaggio motivazionale mensile in banner SOFT (non duplicato)
// - Spaziature compattate secondo richieste (titoli, anno, separatori, sezioni)
// - ESLint clean: niente variabili non usate, niente reference non definite
// - Import/Export solo top-level
// ===================================================================================

import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ==============================
// Tema CEI + Stile/Tipografia
// ==============================
const CEI_COLORS = {
  1: [39, 131, 67],   // verde
  2: [245, 199, 41],  // giallo
  3: [33, 114, 191],  // blu
  4: [226, 66, 62],   // rosso
  5: [133, 76, 175],  // viola
  6: [255, 255, 255], // bianco
  7: [242, 143, 36],  // arancione
};

const common = {
  margin: 20,
  primary: [41, 128, 185],
  text: [0, 0, 0],
  headFont: 'Helvetica',
  titleFont: 'Helvetica',
  tableFont: 'Helvetica',
  titleSize: 16,
  subSize: 12,
  tableFontSize: 10,
  bandH: 18,
  cellPad: 3,
};

export const LONG_ABSENCE_THRESHOLD_DAYS = 21;

// ==============================
// Util colore anno CEI
// ==============================
function parseYearNumber(yearLabel) {
  const m = String(yearLabel || '').match(/\d+/);
  return m ? Math.min(7, Math.max(1, parseInt(m[0], 10))) : null;
}
function headerBg(yearLabel) {
  const n = parseYearNumber(yearLabel);
  return CEI_COLORS[n] || null;
}
function contrastText(rgb) {
  const r = Array.isArray(rgb) ? (rgb[0] ?? 0) : 0;
  const g = Array.isArray(rgb) ? (rgb[1] ?? 0) : 0;
  const b = Array.isArray(rgb) ? (rgb[2] ?? 0) : 0;
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 180 ? [0, 0, 0] : [255, 255, 255];
}

// ==============================
// Helpers Date (senza moment.js)
// ==============================
function toDateSafe(d) {
  try {
    if (!d) return null;
    if (d?.toDate && typeof d.toDate === 'function') return d.toDate();
    if (d instanceof Date) return Number.isFinite(d.getTime()) ? d : null;
    const t = new Date(d);
    return Number.isFinite(t.getTime()) ? t : null;
  } catch {
    return null;
  }
}
function toYMD(d) {
  const dt = toDateSafe(d);
  if (!dt) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fmtDMY(d) {
  const dt = toDateSafe(d);
  if (!dt) return '-';
  const day = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const y = String(dt.getFullYear());
  return `${day}/${m}/${y}`;
}
// SOLO giorno/mese per intestazioni colonne mensile
function fmtDM(d) {
  const dt = toDateSafe(d);
  if (!dt) return '-';
  const day = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  return `${day}/${m}`;
}

// ==============================
// Logo & Immagini
// ==============================
let _cachedLogo = null;

async function loadLogoDataUrl(targetPx = 120) {
  if (_cachedLogo) return _cachedLogo;
  try {
    const resp = await fetch('/Logo.png', { cache: 'force-cache' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const out = await new Promise((resolve) => {
      img.onload = () => {
        try {
          let w = img.width;
          let h = img.height;
          if (w > h && w > targetPx) {
            const s = targetPx / w;
            w = Math.round(w * s);
            h = Math.round(h * s);
          } else if (h >= w && h > targetPx) {
            const s = targetPx / h;
            w = Math.round(w * s);
            h = Math.round(h * s);
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const data = canvas.toDataURL('image/png', 0.9);
          URL.revokeObjectURL(url);
          resolve(data);
        } catch {
          URL.revokeObjectURL(url);
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });

    _cachedLogo = out;
    return out;
  } catch {
    return null;
  }
}

async function getCompressedImageBase64(url, maxSide = 100) {
  try {
    if (!url) return null;
    const resp = await fetch(url, { cache: 'force-cache' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const dataUrl = await new Promise((resolve) => {
      img.onload = () => {
        try {
          let w = img.width;
          let h = img.height;
          const maxDim = Math.max(w, h) || 1;
          const scale = maxDim > maxSide ? (maxSide / maxDim) : 1;
          w = Math.max(1, Math.round(w * scale));
          h = Math.max(1, Math.round(h * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const out = canvas.toDataURL('image/png', 0.9);
          URL.revokeObjectURL(blobUrl);
          resolve(out);
        } catch {
          URL.revokeObjectURL(blobUrl);
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        resolve(null);
      };
      img.src = blobUrl;
    });

    return dataUrl;
  } catch {
    return null;
  }
}

// ==============================
// Header pagina con banda CEI
// ==============================
function drawParishHeader(doc, yearLabel, logoDataUrl) {
  const pageW = doc.internal.pageSize.getWidth();
  const bg = headerBg(yearLabel);
  if (bg) {
    doc.setFillColor(bg[0], bg[1], bg[2]);
    doc.rect(0, 0, pageW, common.bandH, 'F');
  }
  const txtColor = bg ? contrastText(bg) : common.primary;

  const centerY = Math.round(common.bandH / 2) + 1;
  const iconSize = 10;
  const yLogo = Math.max(1, centerY - Math.floor(iconSize / 2));

  try {
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', common.margin, yLogo, iconSize, iconSize);
      doc.addImage(logoDataUrl, 'PNG', pageW - common.margin - iconSize, yLogo, iconSize, iconSize);
    }
  } catch {
    // opzionale
  }

  doc.setFont(common.headFont, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(txtColor[0], txtColor[1], txtColor[2]);
  doc.text(
    'Parrocchia del S.S. Redentore - Monserrato',
    pageW / 2,
    centerY,
    { align: 'center', baseline: 'middle' }
  );
}

// linea orizzontale generica compattata
function hr(doc, y) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(common.margin, y + 2, pageW - common.margin, y + 2);
  return y + 6;
}

function drawSubtitleCentered(doc, text, y) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFont(common.titleFont, 'normal');
  doc.setFontSize(common.subSize);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  doc.text(String(text || ''), pageW / 2, y, { align: 'center' });
  return y + 8;
}

function drawSectionTitle(doc, text, y) {
  doc.setFont(common.titleFont, 'bold');
  doc.setFontSize(12);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  doc.text(String(text || ''), common.margin, y);
  return y + 5; // compattato
}

// ==============================
// Foto Studenti (avatar tondo)
// ==============================
function addStudentAvatar(doc, data) {
  if (data?.cell?.section !== 'body') return;
  if (data?.column?.dataKey !== 'foto') return;

  const img = data.cell.raw;
  const w = 10;
  const h = 10;
  const x = data.cell.x + (data.cell.width - w) / 2;
  const y = data.cell.y + (data.cell.height - h) / 2;

  try {
    if (img) {
      doc.addImage(img, 'PNG', x, y, w, h);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.circle(x + w / 2, y + h / 2, w / 2);
    } else {
      const first = String(data?.row?.raw?.nome || '?').charAt(0).toUpperCase();
      const cx = x + w / 2;
      const cy = y + h / 2;
      doc.setFillColor(230, 230, 230);
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.circle(cx, cy, 5, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.setFont('Helvetica', 'bold');
      doc.text(first, cx, cy + 1, { align: 'center', baseline: 'middle' });
    }
  } catch {
    // ignore
  }
}

function blankPhotoTextHook(data) {
  if (data?.column?.dataKey === 'foto') {
    data.cell.text = '';
  }
}

// ==============================
// PRESENZE: mapping robusto
// ==============================
function pickPresenceByDateOrIndex(student, meetingDate, idx) {
  const ymd = toYMD(meetingDate);

  // 1) Mappe by date
  const maps = [student?.presenzeByDate, student?.attendance, student?.presenceByDate];
  for (let i = 0; i < maps.length; i += 1) {
    const mp = maps[i];
    if (mp && typeof mp === 'object' && Object.prototype.hasOwnProperty.call(mp, ymd)) {
      return !!mp[ymd];
    }
  }

  // 2) Coppia dates + presenze
  const datesArr = Array.isArray(student?.presenzeDates)
    ? student.presenzeDates
    : (Array.isArray(student?.presentDates) ? student.presentDates : null);
  const presArr = Array.isArray(student?.presenze) ? student.presenze : null;

  if (datesArr && presArr && datesArr.length === presArr.length) {
    const k = datesArr.findIndex((d) => toYMD(d) === ymd);
    if (k >= 0) return !!presArr[k];
  }

  // 3) Fallback indice
  if (presArr && Number.isInteger(idx) && idx >= 0 && idx < presArr.length) {
    return !!presArr[idx];
  }

  // 4) Fallback boolean generico
  if (typeof student?.present === 'boolean') return student.present;
  if (typeof student?.presente === 'boolean') return student.presente;

  return false;
}

// ==============================
// UTIL: Frasi alert mensili
// ==============================
function monthlyAlertInfo(avgPresencePct) {
  const p = Math.max(0, Math.min(100, Math.round(avgPresencePct || 0)));
  const pctText = `intorno al ${p}%`;

  if (p >= 75) {
    return {
      text:
        `In questo mese la partecipazione è stata molto viva (${pctText} di presenze). ` +
        `La fedeltà agli incontri racconta un cammino condiviso. Continuiamo a coltivare questo slancio ` +
        `con gratitudine e cura, insieme alle famiglie.`,
      color: [120, 170, 120], // verde soft
    };
  }

  if (p >= 50) {
    return {
      text:
        `In questo mese la partecipazione è stata buona (${pctText} di presenze). ` +
        `Alcune assenze ricordano che ogni famiglia vive tempi e ritmi diversi. Il cammino prosegue ` +
        `grazie all’attenzione e alla pazienza dei catechisti, che continuano a offrire accoglienza ` +
        `e continuità.`,
      color: [245, 206, 120], // giallo soft
    };
  }

  return {
    text:
      `In questo mese la partecipazione è stata ridotta (${pctText} di presenze). ` +
      `Le assenze possono dipendere da impegni e fatiche differenti. I catechisti restano disponibili ` +
      `ad accogliere e accompagnare con pazienza chi rientra gradualmente, mantenendo aperto il dialogo ` +
      `con le famiglie.`,
    color: [230, 140, 140], // rosso soft
  };
}

// ==============================
function drawSoftBanner(doc, opts) {
  const {
    x, y, w, textLines,
    baseColor = [230, 230, 230],
    pad = 4,
    lineH = 5,
    corner = 2,
    bold = true,
    fontSize = 11,
  } = opts;

  const h = pad * 2 + (textLines.length * lineH);

  // Sfondo rettangolo con angoli arrotondati (soft)
  doc.setFillColor(baseColor[0], baseColor[1], baseColor[2]);
  try {
    doc.roundedRect(x, y, w, h, corner, corner, 'F');
  } catch {
    doc.rect(x, y, w, h, 'F');
  }

  // Testo
  const txtColor = contrastText(baseColor);
  doc.setTextColor(txtColor[0], txtColor[1], txtColor[2]);
  doc.setFont(common.titleFont, bold ? 'bold' : 'normal');
  doc.setFontSize(fontSize);

  let yy = y + pad + 3;
  textLines.forEach((ln) => {
    doc.text(String(ln), x + 2, yy);
    yy += lineH;
  });

  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
}

// ===================================================================================
// REPORT: SETTIMANALE
// ===================================================================================
export async function generatePdfReport(
  students = [],
  currentDate = '',
  lessonPlan = '',
  dashboardTitle = 'Dashboard',
  catechist = '',
  yearLabel = ''
) {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const usableW = pageW - 2 * common.margin;
  const logo = await loadLogoDataUrl(120);

  drawParishHeader(doc, yearLabel, logo);

  // meno spazio dopo l'header grafico
  let y = common.margin + 10;

  // Titolo principale (es. "Diario 2025/2026")
  doc.setFont(common.titleFont, 'bold');
  doc.setFontSize(common.titleSize);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  doc.text(String(dashboardTitle || 'Dashboard'), pageW / 2, y, { align: 'center' });
  y += 8;

  // Sottotitolo + Anno catechistico con un filo di interlinea in più
  y = drawSubtitleCentered(
    doc,
    `Catechista: ${String(catechist || '')}    Data dell'incontro: ${String(currentDate || '')}`,
    y
  );
  if (yearLabel) {
    y = drawSubtitleCentered(doc, `Anno catechistico: ${yearLabel}`, y + 3);
  }
  y = hr(doc, y); // linea subito sotto l'anno

  // Programma svolto
  y = drawSectionTitle(doc, 'Programma svolto:', y);
  doc.setFont(common.titleFont, 'normal');
  doc.setFontSize(12);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  const lessonLines = doc.splitTextToSize(String(lessonPlan || '-'), usableW);
  doc.text(lessonLines, common.margin, y, { align: 'justify', maxWidth: usableW });
  y += lessonLines.length * 6.0 + 6; // meno spazio dopo il testo

  // Elenco studenti
  y = drawSectionTitle(doc, 'Elenco Studenti:', y);

  const weeklyColumns = [
    { header: 'Foto', dataKey: 'foto' },
    { header: '#', dataKey: 'numero' },
    { header: 'Cognome', dataKey: 'cognome' },
    { header: 'Nome', dataKey: 'nome' },
    { header: 'Data di Nascita', dataKey: 'dataNascita' },
    { header: 'Presenza', dataKey: 'presenza' },
    { header: 'Privacy', dataKey: 'privacy' },
  ];

  const weeklyBody = await Promise.all(
    (students || []).map(async (s, i) => ({
      foto: s?.photoURL ? await getCompressedImageBase64(s.photoURL, 100) : null,
      numero: i + 1,
      cognome: s?.cognome || '',
      nome: s?.nome || '',
      dataNascita: fmtDMY(s?.dataNascita?.toDate ? s.dataNascita.toDate() : s?.dataNascita),
      presenza: (s?.present || s?.presente) ? 'Sì' : 'No',
      privacy: s?.privacyConsent ? 'Sì' : 'No',
    }))
  );

  const fixedW = {
    foto: 18,
    numero: 16,
    cognome: 34,
    nome: 30,
    dataNascita: 28,
    presenza: 22,
    privacy: 20,
  };
  const totFixed = Object.values(fixedW).reduce((a, b) => a + b, 0);
  const scale = usableW / totFixed || 1;

  const weeklyColumnStyles = {
    foto: { cellWidth: fixedW.foto * scale },
    numero: { halign: 'center', cellWidth: fixedW.numero * scale },
    cognome: { halign: 'left', cellWidth: fixedW.cognome * scale },
    nome: { halign: 'left', cellWidth: fixedW.nome * scale },
    dataNascita: { halign: 'center', cellWidth: fixedW.dataNascita * scale },
    presenza: { halign: 'center', cellWidth: fixedW.presenza * scale },
    privacy: { halign: 'center', cellWidth: fixedW.privacy * scale },
  };

  const headFill = headerBg(yearLabel) || [230, 238, 255];
  const headText = contrastText(headFill);

  doc.autoTable({
    columns: weeklyColumns,
    body: weeklyBody,
    startY: y,
    theme: 'striped',
    styles: {
      font: common.tableFont,
      fontSize: common.tableFontSize,
      cellPadding: common.cellPad, // stretto per far entrare foto/avatar
      valign: 'middle',
      lineHeight: 1.15,
      overflow: 'linebreak',
      minCellHeight: 12,
    },
    headStyles: {
      fillColor: headFill,
      textColor: headText,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      valign: 'middle',
      cellPadding: 2,
      minCellHeight: 10,
      overflow: 'hidden',
    },
    columnStyles: weeklyColumnStyles,
    margin: { left: common.margin, right: common.margin },
    didParseCell: (data) => blankPhotoTextHook(data),
    didDrawCell: (data) => addStudentAvatar(doc, data),
    pageBreak: 'auto',
  });

  const safeCate = String(catechist || '').replace(/[^\w-]/g, '_');
  const safeDate = String(currentDate || '').replace(/\//g, '-').replace(/[^\w-]/g, '_');
  const safeYear = String(yearLabel || '').replace(/[^\w-]/g, '_');
  const fileName = `Report_${safeYear ? `${safeYear}_` : ''}${safeCate}_${safeDate}.pdf`;
  const pdfBlob = doc.output('blob');
  return { pdfBlob, fileName };
}

// ===================================================================================
// REPORT: MENSILE
// ===================================================================================
export async function generateMonthlyPdfReport(
  dashboardTitle,
  catechists,
  additionalTitle,
  monthlyPrograms = [],
  attendanceData = [],
  yearLabel = ''
) {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const usableW = pageW - 2 * common.margin;
  const logo = await loadLogoDataUrl(120);

  drawParishHeader(doc, yearLabel, logo);

  let y = common.margin + 10;

  // Titolo principale
  doc.setFont(common.titleFont, 'bold');
  doc.setFontSize(common.titleSize);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  doc.text(String(dashboardTitle || 'Dashboard'), pageW / 2, y, { align: 'center' });
  y += 8;

  // Sottotitolo + Anno catechistico
  y = drawSubtitleCentered(
    doc,
    `${String(additionalTitle || 'Report Mensile')} - Catechista: ${String(catechists || '')}`,
    y
  );
  if (yearLabel) {
    y = drawSubtitleCentered(doc, `Anno catechistico: ${yearLabel}`, y + 3);
  }
  y = hr(doc, y);

  // Programmi del mese ordinati cronologicamente,
  // mantenendo l'indice ORIGINALE (per allineare le presenze)
  const meetingsSorted = (monthlyPrograms || [])
    .map((p, index) => ({ ...p, __origIndex: index }))
    .sort((a, b) => {
      const at = toDateSafe(a?.meetingDate)?.getTime() ?? 0;
      const bt = toDateSafe(b?.meetingDate)?.getTime() ?? 0;
      return at - bt;
    });

  y = drawSectionTitle(doc, 'Programmi del Mese:', y);
  doc.setFont(common.titleFont, 'normal');
  doc.setFontSize(12);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  const lines = meetingsSorted.map((p, i) => `${i + 1}. ${fmtDMY(p?.meetingDate)}: ${String(p?.lessonPlan || '-')}`);
  const split = doc.splitTextToSize(lines.join('\n'), usableW);
  doc.text(split, common.margin, y, { align: 'justify', maxWidth: usableW });
  y += split.length * 6.0 + 6; // compattato

  // Presenze studenti
  y = drawSectionTitle(doc, 'Presenze Studenti:', y);

  // Colonne presenza: intestazioni in GG/MM
  const presenceCols = meetingsSorted.map((p, idx) => ({
    header: fmtDM(p?.meetingDate) || `P${idx + 1}`,
    dataKey: `p_${idx + 1}`,
  }));

  const columns = [
    { header: 'Foto', dataKey: 'foto' },
    { header: '#', dataKey: 'n' },
    { header: 'Cognome', dataKey: 'cognome' },
    { header: 'Nome', dataKey: 'nome' }, // un po' più larga
    ...presenceCols,
    { header: 'Privacy', dataKey: 'privacy' },
    { header: 'Totale', dataKey: 'tot' },
  ];

  const body = await Promise.all(
    (attendanceData || []).map(async (s, i) => {
      const row = {
        foto: s?.photoURL ? await getCompressedImageBase64(s.photoURL, 100) : null,
        n: i + 1,
        cognome: s?.cognome || '',
        nome: s?.nome || '',
        privacy: s?.privacyConsent ? 'Sì' : 'No',
      };

      let totInline = 0;
      const presArr = Array.isArray(s?.presenze) ? s.presenze : null;
      presenceCols.forEach((col, idx) => {
        const prog = meetingsSorted[idx];
        let isHere = false;

        if (presArr) {
          // Usiamo l'indice ORIGINALE con cui la Dashboard ha costruito s.presenze
          const origIdx = typeof prog.__origIndex === 'number' ? prog.__origIndex : idx;
          if (origIdx >= 0 && origIdx < presArr.length) {
            isHere = !!presArr[origIdx];
          } else {
            // fallback sicuro su data/indice
            isHere = pickPresenceByDateOrIndex(s, prog?.meetingDate, idx);
          }
        } else {
          // se non c'è array presenze, fallback standard
          isHere = pickPresenceByDateOrIndex(s, prog?.meetingDate, idx);
        }

        row[col.dataKey] = isHere ? 'Sì' : 'No';
        if (isHere) totInline += 1;
      });

      // Totale coerente: massimo tra inline e s.totalPresences
      const fallbackTot = Number.isFinite(s?.totalPresences)
        ? Math.max(0, Math.min(Number(s.totalPresences), meetingsSorted.length))
        : totInline;
      row.tot = Math.max(totInline, fallbackTot);

      return row;
    })
  );

  // Larghezze dinamiche
  const fixedBase = { foto: 18, n: 16, cognome: 28, nome: 34, privacy: 18, tot: 16 };
  const baseWidth = fixedBase.foto + fixedBase.n + fixedBase.cognome + fixedBase.nome + fixedBase.privacy + fixedBase.tot;
  const remaining = Math.max(0, usableW - baseWidth);
  const perPresence = presenceCols.length ? Math.max(10, remaining / presenceCols.length) : 0;

  const columnStyles = {
    foto: { cellWidth: fixedBase.foto },
    n: { halign: 'center', cellWidth: fixedBase.n },
    cognome: { halign: 'left', cellWidth: fixedBase.cognome },
    nome: { halign: 'left', cellWidth: fixedBase.nome },
    privacy: { halign: 'center', cellWidth: fixedBase.privacy },
    tot: { halign: 'center', cellWidth: fixedBase.tot },
  };
  presenceCols.forEach((c) => {
    columnStyles[c.dataKey] = { halign: 'center', cellWidth: perPresence };
  });

  const headFill2 = headerBg(yearLabel) || [230, 238, 255];
  const headText2 = contrastText(headFill2);

  doc.autoTable({
    columns,
    body,
    startY: y,
    theme: 'striped',
    styles: {
      font: common.tableFont,
      fontSize: common.tableFontSize,
      cellPadding: common.cellPad,
      valign: 'middle',
      lineHeight: 1.15,
      overflow: 'linebreak',
      minCellHeight: 12,
    },
    headStyles: {
      fillColor: headFill2,
      textColor: headText2,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      valign: 'middle',
      cellPadding: 2,
      minCellHeight: 10,
      overflow: 'hidden',
    },
    columnStyles,
    margin: { left: common.margin, right: common.margin },
    didParseCell: (data) => blankPhotoTextHook(data),
    didDrawCell: (data) => addStudentAvatar(doc, data),
    pageBreak: 'auto',
  });

  // ======================
  // Messaggi mensili (banner soft, NON duplicati)
  // ======================
  const totalMeetings = meetingsSorted.length;
  let avgPresencePct = 0;

  if (totalMeetings > 0 && (attendanceData || []).length) {
    const sumPerc = (attendanceData || []).reduce((sum, s) => {
      let pres = Number.isFinite(s?.totalPresences)
        ? Math.max(0, Math.min(Number(s.totalPresences), totalMeetings))
        : null;
      if (pres === null) {
        pres = 0;
        meetingsSorted.forEach((p, idx) => {
          if (pickPresenceByDateOrIndex(s, p?.meetingDate, idx)) pres += 1;
        });
      }
      const pct = (pres / totalMeetings) * 100;
      return sum + pct;
    }, 0);
    avgPresencePct = Math.round(
      sumPerc / Math.max((attendanceData || []).length, 1)
    );
  }

  const info = monthlyAlertInfo(
    totalMeetings && (attendanceData || []).length ? avgPresencePct : 0
  );
  const baseText = totalMeetings && (attendanceData || []).length
    ? info.text
    : 'In questo mese non sono presenti dati sufficienti per valutare la partecipazione, ma continuiamo a custodire con fiducia il cammino dei bambini e delle loro famiglie.';

  const bannerLines = doc.splitTextToSize(baseText, usableW);
  const afterTableY = (doc.lastAutoTable && doc.lastAutoTable.finalY)
    ? doc.lastAutoTable.finalY + 8
    : y + 8;

  drawSoftBanner(doc, {
    x: common.margin,
    y: afterTableY,
    w: usableW,
    textLines: bannerLines,
    baseColor: totalMeetings && (attendanceData || []).length ? info.color : [230, 230, 230],
    pad: 4,
    lineH: 5,
    corner: 2,
    bold: true,
    fontSize: 11,
  });

  const safeCats = String(catechists || '').replace(/[^\w-]/g, '_');
  const safeAdd = String(additionalTitle || '')
    .replace(/\//g, '-')
    .replace(/[^\w-]/g, '_');
  const safeYear = String(yearLabel || '').replace(/[^\w-]/g, '_');
  const fileName = `Report_${safeYear ? `${safeYear}_` : ''}${safeCats}_${safeAdd}.pdf`;
  const pdfBlob = doc.output('blob');
  return { pdfBlob, fileName };
}

// ===================================================================================
// REPORT: STATISTICHE
// ===================================================================================
export async function generateStatsPdfReport(
  payload = {},
  _fileNameHint,
  yearLabel = ''
) {
  const {
    dashboardTitle = 'Dashboard',
    catechists = '',
    asOfDateLabel = '',
    students = [],
    totalMeetings = 0,
    presenceMap = new Map(),
  } = payload;

  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const usableW = pageW - 2 * common.margin;
  const logo = await loadLogoDataUrl(120);

  drawParishHeader(doc, yearLabel, logo);

  let y = common.margin + 10;

  doc.setFont(common.titleFont, 'bold');
  doc.setFontSize(common.titleSize);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  doc.text(
    `${String(dashboardTitle)} — Statistiche Presenze`,
    pageW / 2,
    y,
    { align: 'center' }
  );
  y += 8;

  y = drawSubtitleCentered(doc, `Catechisti: ${String(catechists)}`, y);
  y = drawSubtitleCentered(
    doc,
    `Periodo considerato: ${String(asOfDateLabel)}`,
    y - 2
  );
  if (yearLabel) {
    y = drawSubtitleCentered(doc, `Anno catechistico: ${yearLabel}`, y - 2);
  }
  y = hr(doc, y);

  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);
  doc.text(
    `Totale incontri considerati: ${Number(totalMeetings) || 0}`,
    common.margin,
    y
  );
  y += 8;

  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const alertText = `Alert assenze prolungate: assenza continuativa > ${LONG_ABSENCE_THRESHOLD_DAYS} giorni.`;
  const alertLines = doc.splitTextToSize(alertText, usableW);
  doc.text(alertLines, common.margin, y, { align: 'justify', maxWidth: usableW });
  y += alertLines.length * 4.5 + 6;
  doc.setTextColor(common.text[0], common.text[1], common.text[2]);

  const columns = [
    { header: 'Foto', dataKey: 'foto' },
    { header: '#', dataKey: 'n' },
    { header: 'Cognome', dataKey: 'cognome' },
    { header: 'Nome', dataKey: 'nome' },
    { header: 'Presenze', dataKey: 'presenze' },
    { header: '%', dataKey: 'perc' },
    { header: 'Privacy', dataKey: 'privacy' },
  ];

  const body = await Promise.all(
    (students || []).map(async (s, i) => {
      let pres = 0;
      if (presenceMap instanceof Map && presenceMap.has(s.id)) {
        pres = Number(presenceMap.get(s.id)) || 0;
      } else if (Array.isArray(s?.presenze)) {
        pres = s.presenze.reduce(
          (acc, v) => acc + (v ? 1 : 0),
          0
        );
      } else if (Number.isFinite(s?.totalPresences)) {
        pres = Math.max(0, Number(s.totalPresences));
      }

      const denom = totalMeetings > 0 ? Number(totalMeetings) : 0;
      const perc = denom > 0 ? Math.round((pres / denom) * 100) : 0;

      return {
        foto: s?.photoURL ? await getCompressedImageBase64(s.photoURL, 100) : null,
        n: i + 1,
        cognome: s?.cognome || '',
        nome: s?.nome || '',
        presenze: String(pres),
        perc: `${perc}%`
        ,
        privacy: s?.privacyConsent ? 'Sì' : 'No',
      };
    })
  );

  const fixed = {
    foto: 18,
    n: 16,
    cognome: 32,
    nome: 30,
    presenze: 22,
    perc: 18,
    privacy: 20,
  };
  const totFixed = Object.values(fixed).reduce((a, b) => a + b, 0);
  const scale = usableW / totFixed || 1;

  const columnStyles = {
    foto: { cellWidth: fixed.foto * scale },
    n: { halign: 'center', cellWidth: fixed.n * scale },
    cognome: { halign: 'left', cellWidth: fixed.cognome * scale },
    nome: { halign: 'left', cellWidth: fixed.nome * scale },
    presenze: { halign: 'center', cellWidth: fixed.presenze * scale },
    perc: { halign: 'center', cellWidth: fixed.perc * scale },
    privacy: { halign: 'center', cellWidth: fixed.privacy * scale },
  };

  const headFill = headerBg(yearLabel) || [230, 238, 255];
  const headText = contrastText(headFill);

  doc.autoTable({
    columns,
    body,
    startY: y,
    theme: 'striped',
    styles: {
      font: common.tableFont,
      fontSize: common.tableFontSize,
      cellPadding: common.cellPad,
      valign: 'middle',
      lineHeight: 1.15,
      overflow: 'linebreak',
      minCellHeight: 12,
    },
    headStyles: {
      fillColor: headFill,
      textColor: headText,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      valign: 'middle',
      cellPadding: 2,
      minCellHeight: 10,
      overflow: 'hidden',
    },
    columnStyles,
    margin: { left: common.margin, right: common.margin },
    didParseCell: (data) => blankPhotoTextHook(data),
    didDrawCell: (data) => addStudentAvatar(doc, data),
    pageBreak: 'auto',
  });

  const safeCats = String(catechists || '').replace(/[^\w-]/g, '_');
  const safeAdd = String(asOfDateLabel || '')
    .replace(/\//g, '-')
    .replace(/[^\w-]/g, '_');
  const safeYear = String(yearLabel || '').replace(/[^\w-]/g, '_');
  const fileName = `Statistiche_presenze_${safeYear ? `${safeYear}_` : ''}${safeCats}_${safeAdd}.pdf`;
  const pdfBlob = doc.output('blob');
  return { pdfBlob, fileName };
}
